import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import ErrorBanner from '@/components/ErrorBanner'
import { useJobRuns } from '@/hooks/useJobRuns'
import { formatTime, relativeTime } from '@/lib/utils'
import { Badge, Card, EmptyState, StatCard, Table } from '@/admin-design/_common'
import type { JobRun } from '@/api/types'

function outcomeBadge(outcome: string) {
  switch (outcome) {
    case 'ok':
      return <Badge variant="success">ok</Badge>
    case 'error':
      return <Badge variant="destructive">error</Badge>
    case 'skipped':
      return <Badge variant="muted">skipped</Badge>
    default:
      return <Badge variant="muted">{outcome || '-'}</Badge>
  }
}

function jobDuration(j: JobRun) {
  if (!j.finished_at || !j.started_at) return '-'
  const ms = (j.finished_at - j.started_at) * 1000
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

function statValue(value: number | string | undefined | null, loading: boolean): string | number {
  if (loading) return '…'
  if (value === null || value === undefined) return '-'
  return value
}

export default function Dashboard() {
  const stats = useQuery({
    queryKey: ['stats'],
    queryFn: api.stats,
    refetchInterval: 10_000,
  })
  const jobs = useJobRuns({ limit: 20 })
  const storage = useQuery({
    queryKey: ['storage-stats'],
    queryFn: api.storageStats,
    refetchInterval: 30_000,
  })

  const failedJobs = stats.data?.failed_jobs_24h ?? 0

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Dashboard</h1>

      {stats.error && <ErrorBanner error={stats.error} />}

      <Card>
        <Card.Body className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-sm font-semibold">소비자 API · 태그 사용법</h2>
            <span className="text-xs text-muted-foreground">
              studio·quant가 토픽을 가져가는 방법
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            인증: <code>X-API-Key: &lt;키&gt;</code> (Consumers 탭에서 발급). 태그로 필요한
            카테고리만 걸러서 가져간다.
          </p>
          <ul className="space-y-0.5 text-xs">
            <li>
              <code>GET /api/topics</code> — <b>클러스터링된 토픽</b> (유사 글 묶음, ~10분마다
              재클러스터링). studio 팩토리가 여기서 소재를 뽑는다.
            </li>
            <li>
              <code>{'GET /api/topics/{id}'}</code> — 토픽 1개 + 대표 아이템 top10.
            </li>
            <li>
              <code>GET /api/trends</code> — <b>raw 아이템</b> (미클러스터).
            </li>
          </ul>
          <p className="text-xs">
            <b>파라미터</b>: <code>?tag=</code>(카테고리 CSV, OR매칭) · <code>source=</code> ·{' '}
            <code>min_score=</code> · <code>since=</code>(RFC3339/epoch) · <code>limit=</code> ·{' '}
            <code>cursor=</code> · <code>include_items=1</code>
          </p>
          <p className="text-xs">
            <b>태그</b>:{' '}
            <code>community · news · sns · korean · global · tech · crypto · gaming · trends</code>
          </p>
          <p className="text-xs text-muted-foreground">
            예) 커뮤니티 글만 → <code>{'GET /api/topics?tag=community&include_items=1'}</code> ·
            SDK엔 tag가 없어 raw GET 사용. 상세: repo <code>docs/consumer-tags.md</code>
          </p>
        </Card.Body>
      </Card>

      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          label="Topics (total)"
          value={statValue(stats.data?.total_topics, stats.isLoading)}
          hint={
            stats.data ? `${stats.data.topics_24h.toLocaleString('ko-KR')} new in 24h` : undefined
          }
        />
        <StatCard label="Items (24h)" value={statValue(stats.data?.items_24h, stats.isLoading)} />
        <StatCard
          label="Failed jobs (24h)"
          value={statValue(stats.data?.failed_jobs_24h, stats.isLoading)}
          tone={failedJobs > 0 ? 'warning' : undefined}
        />
        <StatCard
          label="Active sources"
          value={statValue(stats.data?.enabled_sources, stats.isLoading)}
        />
      </section>

      {stats.data && stats.data.top_sources_24h.length > 0 && (
        <Card>
          <Card.Body>
            <h2 className="text-sm font-semibold text-foreground mb-2">Top sources (24h)</h2>
            <ul className="text-sm space-y-1">
              {stats.data.top_sources_24h.map((s) => (
                <li
                  key={s.slug}
                  className="flex justify-between border-b border-border last:border-0 py-1"
                >
                  <span className="font-mono text-foreground">{s.slug}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {s.count.toLocaleString('ko-KR')}
                  </span>
                </li>
              ))}
            </ul>
          </Card.Body>
        </Card>
      )}

      <section>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-foreground">Recent job runs</h2>
          {jobs.isFetching && <span className="text-xs text-muted-foreground">refreshing…</span>}
        </div>
        {jobs.error && <ErrorBanner error={jobs.error} />}
        <Card className="overflow-x-auto">
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.Header>Started</Table.Header>
                <Table.Header>Name</Table.Header>
                <Table.Header>Outcome</Table.Header>
                <Table.Header>Duration</Table.Header>
                <Table.Header>Error</Table.Header>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {jobs.isLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <Table.Row key={i}>
                    <Table.Cell colSpan={5}>
                      <span className="inline-block h-4 w-full rounded bg-muted animate-pulse" />
                    </Table.Cell>
                  </Table.Row>
                ))
              ) : (jobs.data?.data?.length ?? 0) === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={5} className="p-0">
                    <EmptyState title="No job runs yet." />
                  </Table.Cell>
                </Table.Row>
              ) : (
                jobs.data?.data?.map((j) => (
                  <Table.Row key={j.id}>
                    <Table.Cell
                      className="whitespace-nowrap text-muted-foreground"
                      title={formatTime(j.started_at)}
                    >
                      {relativeTime(j.started_at)}
                    </Table.Cell>
                    <Table.Cell className="font-mono text-xs">{j.name}</Table.Cell>
                    <Table.Cell>{outcomeBadge(j.outcome)}</Table.Cell>
                    <Table.Cell className="tabular-nums text-muted-foreground">
                      {jobDuration(j)}
                    </Table.Cell>
                    <Table.Cell
                      className="text-xs text-destructive max-w-xs truncate"
                      title={j.error}
                    >
                      {j.error || ''}
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table>
        </Card>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-foreground mb-2">Storage</h2>
        {storage.data && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard label="DB size" value={`${storage.data.db_size_mb.toFixed(1)} MB`} />
              <StatCard
                label="Total items"
                value={storage.data.total_items.toLocaleString('ko-KR')}
              />
              <StatCard label="Topics" value={storage.data.total_topics.toLocaleString('ko-KR')} />
              <StatCard
                label="Job runs"
                value={storage.data.total_job_runs.toLocaleString('ko-KR')}
              />
            </div>
            {storage.data.per_source.length > 0 && (
              <Card className="overflow-x-auto">
                <Table>
                  <Table.Head>
                    <Table.Row>
                      <Table.Header>Source</Table.Header>
                      <Table.Header className="text-right">Items</Table.Header>
                      <Table.Header className="text-right">Retention</Table.Header>
                      <Table.Header className="text-right">Range</Table.Header>
                    </Table.Row>
                  </Table.Head>
                  <Table.Body>
                    {storage.data.per_source.map((s) => (
                      <Table.Row key={s.slug}>
                        <Table.Cell className="font-mono text-xs">{s.slug}</Table.Cell>
                        <Table.Cell className="text-right tabular-nums">
                          {s.item_count.toLocaleString('ko-KR')}
                        </Table.Cell>
                        <Table.Cell className="text-right text-muted-foreground">
                          {s.retention_days}d
                        </Table.Cell>
                        <Table.Cell className="text-right text-xs text-muted-foreground">
                          {s.oldest_at && s.newest_at
                            ? `${relativeTime(s.oldest_at)} ~ ${relativeTime(s.newest_at)}`
                            : '-'}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </Card>
            )}
          </div>
        )}
      </section>
    </div>
  )
}
